/**
 * Salary Net Calculator — Calculation Engine (2026 rates)
 * Sources: CRA (canada.ca/taxes), Revenu Québec (revenuquebec.ca)
 */
import { TAX_DATA, getMarginalRate } from './taxBrackets';

const BASIC_PERSONAL = {
  federal: 16452,
  QC: 18952, ON: 12989, BC: 13216, AB: 22769,
  SK: 20381, MB: 15780, NB: 13664, NS: 11932, PE: 15000, NL: 11188,
};

// QPP 2026 (Quebec) — revenuquebec.ca
const QPP = {
  rate1: 0.0640,
  rate2: 0.0400,
  exemption: 3500,
  max1: 74600,
  max2: 85000,
};

// CPP 2026 (other provinces) — canada.ca/cpp-contribution-rates
const CPP = {
  rate1: 0.0595,
  rate2: 0.0400,
  exemption: 3500,
  max1: 74600,
  max2: 85000,
};

// EI 2026 (canada.ca/ei-premium-rates-maximums)
const EI = {
  rateQC: 0.01300,
  rateOther: 0.01630,
  maxInsurable: 68900,
};

// QPIP 2026 (Quebec only — rqap.gouv.qc.ca, rate reduced 13% for 2026)
const QPIP = {
  rate: 0.00455,
  maxInsurable: 103000,
};

const calcBracketTax = (income, brackets, personalAmount) => {
  // Calculate tax on full income
  let tax = 0;
  let remaining = income;
  for (let i = 0; i < brackets.length; i++) {
    const bracketSize = (brackets[i].max || Infinity) - brackets[i].min;
    const taxableInBracket = Math.min(remaining, bracketSize);
    tax += taxableInBracket * brackets[i].rate;
    remaining -= taxableInBracket;
    if (remaining <= 0) break;
  }
  // Apply BPA as non-refundable credit (BPA × lowest bracket rate)
  const lowestRate = brackets[0]?.rate || 0;
  const bpaCredit = personalAmount * lowestRate;
  return Math.max(tax - bpaCredit, 0);
};

export const calcNetSalary = (gross, province = 'QC', rrspDeduction = 0) => {
  const taxableIncome = Math.max(gross - rrspDeduction, 0);

  // Federal tax
  let fedTax = calcBracketTax(taxableIncome, TAX_DATA.federal, BASIC_PERSONAL.federal);
  const abatement = TAX_DATA.provinces[province]?.abatement || 0;
  if (abatement > 0) fedTax *= (1 - abatement);

  // Provincial tax
  const provData = TAX_DATA.provinces[province];
  const provPersonal = BASIC_PERSONAL[province] || 10000;
  const provTax = provData
    ? calcBracketTax(taxableIncome, provData.brackets, provPersonal)
    : 0;

  // QPP or CPP
  const pensionConfig = province === 'QC' ? QPP : CPP;
  const pensionable1 = Math.max(Math.min(gross, pensionConfig.max1) - pensionConfig.exemption, 0);
  const pension1 = pensionable1 * pensionConfig.rate1;
  const pensionable2 = Math.max(Math.min(gross, pensionConfig.max2) - pensionConfig.max1, 0);
  const pension2 = pensionable2 * pensionConfig.rate2;
  const totalPension = pension1 + pension2;

  // EI
  const eiRate = province === 'QC' ? EI.rateQC : EI.rateOther;
  const ei = Math.min(gross * eiRate, EI.maxInsurable * eiRate);

  // QPIP (Quebec only)
  const qpip = province === 'QC' ? Math.min(gross * QPIP.rate, QPIP.maxInsurable * QPIP.rate) : 0;

  // RRSP tax saving
  const marginalRate = getMarginalRate(taxableIncome, province);
  const rrspTaxSaving = rrspDeduction * marginalRate;

  // Totals
  const totalDeductions = fedTax + provTax + totalPension + ei + qpip;
  const net = gross - totalDeductions;
  const effectiveRate = gross > 0 ? totalDeductions / gross : 0;

  return {
    gross,
    federalTax: Math.round(fedTax),
    provincialTax: Math.round(provTax),
    pension: Math.round(totalPension),
    pensionLabel: province === 'QC' ? 'QPP' : 'CPP',
    ei: Math.round(ei),
    qpip: Math.round(qpip),
    totalDeductions: Math.round(totalDeductions),
    net: Math.round(net),
    effectiveRate: (effectiveRate * 100).toFixed(1),
    marginalRate: (marginalRate * 100).toFixed(1),
    monthly: Math.round(net / 12),
    biweekly: Math.round(net / 26),
    weekly: Math.round(net / 52),
    rrspTaxSaving: Math.round(rrspTaxSaving),
  };
};
